/**
 * Ed25519 Signature Verification for HTTP Message Signatures
 * Implements proper cryptographic verification for ChatGPT and other agents
 *
 * Based on RFC 9421 (HTTP Message Signatures) and ChatGPT's implementation
 * Reference: https://help.openai.com/en/articles/9785974-chatgpt-user-allowlisting
 */

import * as ed25519 from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha2.js';

// Configure @noble/ed25519 to use sync SHA-512 from @noble/hashes
// This works in all environments including Edge Runtime
ed25519.etc.sha512Sync = (...m) => sha512(ed25519.etc.concatBytes(...m));

/**
 * Known public keys for AI agents (fallback)
 *
 * IMPORTANT: These keys are used as fallback when the API is unavailable.
 * The primary source of keys should be the /api/internal/signature-keys endpoint
 * which fetches from https://chatgpt.com/.well-known/http-message-signatures-directory
 *
 * TODO: Implement automated key rotation by:
 * 1. Setting up a cron job to fetch from OpenAI's well-known endpoint
 * 2. Storing keys in database/KV store with proper expiration handling
 * 3. Removing hardcoded fallback keys entirely
 */
const KNOWN_KEYS = {
  chatgpt: [
    {
      kid: 'otMqcjr17mGyruktGvJU8oojQTSMHlVm7uO-lrcqbdg',
      // ChatGPT's current Ed25519 public key (base64)
      // Source: https://chatgpt.com/.well-known/http-message-signatures-directory
      publicKey: '7F_3jDlxaquwh291MiACkcS3Opq88NksyHiakzS-Y1g',
      validFrom: 1735689600, // Jan 1, 2025 (nbf from OpenAI)
      validUntil: 1769029093, // Jan 21, 2026 (exp from OpenAI)
    },
  ],
};

/**
 * In-memory cache for API-fetched keys
 */
interface CachedKeys {
  keys: Array<{
    kid: string;
    publicKey: string;
    validFrom: number;
    validUntil: number;
  }>;
  cachedAt: number;
}

const keyCache = new Map<string, CachedKeys>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_MAX_SIZE = 100; // Maximum cache entries before cleanup

/**
 * Get API base URL for fetching keys
 * Returns absolute URL for server-side, relative for browser
 */
function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    // Browser: use relative path
    return '/api/internal';
  }

  // Server-side: must use absolute URL
  // Try environment variables first
  const baseUrl =
    process.env.NEXT_PUBLIC_APP_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.API_URL ||
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null);

  if (baseUrl) {
    return baseUrl.replace(/\/$/, '') + '/api/internal';
  }

  // Fallback: try to construct from request context if available
  // For middleware/edge runtime, we may need to pass the request URL
  // For now, return relative path and log warning
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[Signature] No base URL configured for server-side fetch. Using localhost fallback.'
    );
    return 'http://localhost:3000/api/internal';
  }

  // Production fallback - should not reach here if properly configured
  console.error(
    '[Signature] CRITICAL: No base URL configured for server-side fetch in production!'
  );
  return '/api/internal'; // Will fail, but prevents silent success
}

/**
 * Clean up expired cache entries and enforce size limit
 * Called periodically to prevent unbounded memory growth
 * Uses LRU-style eviction: removes expired entries first, then oldest entries if still over limit
 */
function cleanupExpiredCache(): void {
  const now = Date.now();
  const entriesToDelete: string[] = [];

  // First pass: remove expired entries
  for (const [agent, cached] of keyCache.entries()) {
    if (now - cached.cachedAt > CACHE_TTL_MS) {
      entriesToDelete.push(agent);
    }
  }

  for (const agent of entriesToDelete) {
    keyCache.delete(agent);
  }

  // Second pass: if still over limit, remove oldest entries (LRU eviction)
  if (keyCache.size > CACHE_MAX_SIZE) {
    // Convert entries to array with cachedAt timestamp for sorting
    const entries = Array.from(keyCache.entries()).map(([agent, cached]) => ({
      agent,
      cachedAt: cached.cachedAt,
    }));

    // Sort by cachedAt (oldest first)
    entries.sort((a, b) => a.cachedAt - b.cachedAt);

    // Remove oldest entries until we're under the limit
    const toRemove = entries.slice(0, keyCache.size - CACHE_MAX_SIZE);
    for (const entry of toRemove) {
      keyCache.delete(entry.agent);
    }
  }
}

/**
 * Fetch keys from API with caching
 */
async function fetchKeysFromApi(agent: string): Promise<Array<{
  kid: string;
  publicKey: string;
  validFrom: number;
  validUntil: number;
}> | null> {
  // Periodic cleanup to prevent memory leaks
  if (keyCache.size > CACHE_MAX_SIZE) {
    cleanupExpiredCache();
  }

  // Check cache first
  const cached = keyCache.get(agent);
  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.keys;
  }

  // Check if fetch is available (Edge Runtime compatibility)
  if (typeof fetch === 'undefined') {
    console.warn('[Signature] fetch() not available in this environment');
    return null;
  }

  try {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/signature-keys?agent=${encodeURIComponent(agent)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // 5 second timeout
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      console.warn(`[Signature] Failed to fetch keys from API: ${response.status}`);
      return null;
    }

    const data = await response.json();

    if (!data.keys || !Array.isArray(data.keys) || data.keys.length === 0) {
      console.warn(`[Signature] No keys returned from API for agent: ${agent}`);
      return null;
    }

    // Cache the result
    keyCache.set(agent, {
      keys: data.keys,
      cachedAt: Date.now(),
    });

    return data.keys;
  } catch (error) {
    console.warn('[Signature] Error fetching keys from API, using fallback', {
      error: error instanceof Error ? error.message : 'Unknown error',
      agent,
    });
    return null;
  }
}

/**
 * Type guard to check if agent is a valid key in KNOWN_KEYS
 */
function isValidAgent(agent: string): agent is keyof typeof KNOWN_KEYS {
  return agent in KNOWN_KEYS;
}

/**
 * Get keys for an agent (API first, then fallback)
 */
async function getKeysForAgent(agent: string): Promise<
  Array<{
    kid: string;
    publicKey: string;
    validFrom: number;
    validUntil: number;
  }>
> {
  // Try API first
  const apiKeys = await fetchKeysFromApi(agent);
  if (apiKeys && apiKeys.length > 0) {
    return apiKeys;
  }

  // Fallback to hardcoded keys with type guard
  if (isValidAgent(agent)) {
    return KNOWN_KEYS[agent];
  }

  return [];
}

/**
 * Parse the Signature-Input header according to RFC 9421
 */
function parseSignatureInput(signatureInput: string): {
  keyid: string;
  created?: number | undefined;
  expires?: number | undefined;
  signedHeaders: string[];
} | null {
  try {
    // Example: sig1=("@method" "@path" "@authority" "date");keyid="...";created=1234567890
    const match = signatureInput.match(/sig1=\((.*?)\);(.+)/);
    if (!match) return null;

    const [, headersList, params] = match;

    // Parse signed headers
    const signedHeaders = headersList
      ? headersList
          .split(' ')
          .map((h) => h.replace(/"/g, '').trim())
          .filter((h) => h.length > 0)
      : [];

    // Parse parameters
    const keyidMatch = params ? params.match(/keyid="([^"]+)"/) : null;
    const createdMatch = params ? params.match(/created=(\d+)/) : null;
    const expiresMatch = params ? params.match(/expires=(\d+)/) : null;

    if (!keyidMatch || !keyidMatch[1]) return null;

    return {
      keyid: keyidMatch[1],
      created: createdMatch && createdMatch[1] ? parseInt(createdMatch[1]) : undefined,
      expires: expiresMatch && expiresMatch[1] ? parseInt(expiresMatch[1]) : undefined,
      signedHeaders,
    };
  } catch (error) {
    console.error('[Signature] Failed to parse Signature-Input:', error);
    return null;
  }
}

/**
 * Build the signature base string according to RFC 9421
 * This is what gets signed
 */
function buildSignatureBase(
  method: string,
  path: string,
  headers: Record<string, string>,
  signedHeaders: string[]
): string {
  const components: string[] = [];

  for (const headerName of signedHeaders) {
    let value: string;

    switch (headerName) {
      case '@method':
        value = method.toUpperCase();
        break;
      case '@path':
        value = path;
        break;
      case '@authority':
        // Get from Host header or URL
        value = headers['host'] || headers['Host'] || '';
        break;
      default: {
        // Regular headers (case-insensitive lookup)
        const key = Object.keys(headers).find((k) => k.toLowerCase() === headerName.toLowerCase());
        value = key ? headers[key] || '' : '';
        break;
      }
    }

    // Format according to RFC 9421
    components.push(`"${headerName}": ${value}`);
  }

  return components.join('\n');
}

/**
 * Decode base64 (handles both standard and URL-safe variants)
 * URL-safe base64 uses - instead of + and _ instead of /
 */
function base64ToBytes(base64: string): Uint8Array {
  // Convert URL-safe base64 to standard base64
  let standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padding = standardBase64.length % 4;
  if (padding) {
    standardBase64 += '='.repeat(4 - padding);
  }

  const binaryString = atob(standardBase64);
  return Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
}

/**
 * Verify Ed25519 signature using @noble/ed25519 (works in all environments including Edge Runtime)
 * Falls back to Web Crypto API if available
 */
async function verifyEd25519Signature(
  publicKeyBase64: string,
  signatureBase64: string,
  message: string
): Promise<boolean> {
  try {
    // Decode base64 to Uint8Array (handles URL-safe base64)
    const publicKeyBytes = base64ToBytes(publicKeyBase64);
    const signatureBytes = base64ToBytes(signatureBase64);
    const messageBytes = new TextEncoder().encode(message);

    // Check key and signature lengths
    if (publicKeyBytes.length !== 32) {
      console.error('[Signature] Invalid public key length:', publicKeyBytes.length);
      return false;
    }
    if (signatureBytes.length !== 64) {
      console.error('[Signature] Invalid signature length:', signatureBytes.length);
      return false;
    }

    // Use @noble/ed25519 with sync SHA-512 - works in all environments including Edge Runtime
    return ed25519.verify(signatureBytes, messageBytes, publicKeyBytes);
  } catch (nobleError) {
    console.warn('[Signature] @noble/ed25519 failed, trying Web Crypto fallback:', nobleError);

    // Fallback to Web Crypto API (may not work in Edge Runtime)
    try {
      const publicKeyBytes = base64ToBytes(publicKeyBase64);
      const signatureBytes = base64ToBytes(signatureBase64);
      const messageBytes = new TextEncoder().encode(message);

      const publicKey = await crypto.subtle.importKey(
        'raw',
        publicKeyBytes.buffer as ArrayBuffer,
        {
          name: 'Ed25519',
          namedCurve: 'Ed25519',
        },
        false,
        ['verify']
      );

      return await crypto.subtle.verify(
        'Ed25519',
        publicKey,
        signatureBytes.buffer as ArrayBuffer,
        messageBytes
      );
    } catch (cryptoError) {
      console.error('[Signature] Both @noble/ed25519 and Web Crypto failed:', {
        nobleError: nobleError instanceof Error ? nobleError.message : 'Unknown',
        cryptoError: cryptoError instanceof Error ? cryptoError.message : 'Unknown',
      });
      return false;
    }
  }
}

/**
 * Signature verification result
 */
export interface SignatureVerificationResult {
  isValid: boolean;
  agent?: string;
  keyid?: string;
  confidence: number;
  reason?: string;
  verificationMethod: 'signature' | 'none';
}

/**
 * Verify HTTP Message Signature for AI agents
 */
export async function verifyAgentSignature(
  method: string,
  path: string,
  headers: Record<string, string>
): Promise<SignatureVerificationResult> {
  // Check for signature headers
  const signature = headers['signature'] || headers['Signature'];
  const signatureInput = headers['signature-input'] || headers['Signature-Input'];
  const signatureAgent = headers['signature-agent'] || headers['Signature-Agent'];

  // No signature present
  if (!signature || !signatureInput) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'No signature headers present',
      verificationMethod: 'none',
    };
  }

  // Parse Signature-Input header
  const parsed = parseSignatureInput(signatureInput);
  if (!parsed) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Invalid Signature-Input header',
      verificationMethod: 'none',
    };
  }

  // Check timestamp if present
  if (parsed.created) {
    const now = Math.floor(Date.now() / 1000);
    const age = now - parsed.created;

    // Reject signatures older than 5 minutes
    if (age > 300) {
      return {
        isValid: false,
        confidence: 0,
        reason: 'Signature expired (older than 5 minutes)',
        verificationMethod: 'none',
      };
    }

    // Reject signatures from the future (clock skew tolerance: 30 seconds)
    if (age < -30) {
      return {
        isValid: false,
        confidence: 0,
        reason: 'Signature timestamp is in the future',
        verificationMethod: 'none',
      };
    }
  }

  // Determine which agent based on signature-agent header
  let agent: string | undefined;
  let agentKey: string | undefined;

  if (signatureAgent === '"https://chatgpt.com"' || signatureAgent?.includes('chatgpt.com')) {
    agent = 'ChatGPT';
    agentKey = 'chatgpt';
  }
  // Add other agents here as needed

  if (!agent || !agentKey) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Unknown signature agent',
      verificationMethod: 'none',
    };
  }

  // Get keys (API first, then fallback)
  const knownKeys = await getKeysForAgent(agentKey);

  if (knownKeys.length === 0) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'No keys available for agent',
      verificationMethod: 'none',
    };
  }

  // Find the key by ID
  const key = knownKeys.find((k) => k.kid === parsed.keyid);
  if (!key) {
    return {
      isValid: false,
      confidence: 0,
      reason: `Unknown key ID: ${parsed.keyid}`,
      verificationMethod: 'none',
    };
  }

  // Check key validity period
  const now = Math.floor(Date.now() / 1000);
  if (now < key.validFrom || now > key.validUntil) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Key is not valid at current time',
      verificationMethod: 'none',
    };
  }

  // Build the signature base string
  const signatureBase = buildSignatureBase(method, path, headers, parsed.signedHeaders);

  // Extract the actual signature value (remove "sig1=:" prefix and "::" suffix if present)
  let signatureValue = signature;
  if (signatureValue.startsWith('sig1=:')) {
    signatureValue = signatureValue.substring(6);
  }
  if (signatureValue.endsWith(':')) {
    signatureValue = signatureValue.slice(0, -1);
  }

  // Verify the signature
  const isValid = await verifyEd25519Signature(key.publicKey, signatureValue, signatureBase);

  if (isValid) {
    return {
      isValid: true,
      agent,
      keyid: parsed.keyid,
      confidence: 1.0, // 100% confidence for valid signature
      verificationMethod: 'signature',
    };
  } else {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Signature verification failed',
      verificationMethod: 'none',
    };
  }
}

/**
 * Quick check if signature headers are present (for performance)
 */
export function hasSignatureHeaders(headers: Record<string, string>): boolean {
  return !!(
    (headers['signature'] || headers['Signature']) &&
    (headers['signature-input'] || headers['Signature-Input'])
  );
}

/**
 * Check if this is a ChatGPT signature based on headers
 * Uses secure URL parsing to prevent spoofing attacks
 */
export function isChatGPTSignature(headers: Record<string, string>): boolean {
  const signatureAgent = headers['signature-agent'] || headers['Signature-Agent'];

  if (!signatureAgent) {
    return false;
  }

  // Strip leading/trailing quotes if present
  const agentUrlStr = signatureAgent.replace(/^"+|"+$/g, '');

  // Exact match for the standard ChatGPT signature agent
  if (agentUrlStr === 'https://chatgpt.com') {
    return true;
  }

  // Parse URL and validate host to prevent spoofing
  try {
    const agentUrl = new URL(agentUrlStr);
    const allowedHosts = ['chatgpt.com', 'www.chatgpt.com'];
    return allowedHosts.includes(agentUrl.host);
  } catch {
    // Not a valid URL, return false for security
    return false;
  }
}
